<template>
  <unauthed-layout
    :random="getRandomNumberBetween(1, 6)"
    :position="createdAccount ? 'sm:justify-start' : 'sm:justify-center'">
    <template v-slot:content>
      <div class="flex">
        <form
          class="w-full"
          aria-label="Log In Form"
          novalidate>
          <div :class="['px-0', form !== 'login' ? 'mb-16' : null]">
            <h1>
              {{ title }}
            </h1>
          </div>
          <div
            v-if="form === 'login' || form === 'create'"
            class="px-0">
            <span
              id="subtitle"
              class="text-16">
              {{ form === "login"
                ? $t("user_login.subtitle")
                : form === "create"
                  ? $t("user_login.subtitle_create")
                  : "" }}
            </span>
          </div>
          <template v-if="createdAccount">
            <div
              class="mb-16 mt-30 rounded-[3px] bg-dark-green-10 px-24 py-16 text-dark-green">
              {{ $t("custom_classroom.create_success") }}
            </div>
          </template>
          <template v-else>
            <div>
              <div class="mb-16 rounded-6 border border-grey-4 pt-12">
                <span class="p-float-label">
                  <i
                    v-if="form === 'login'"
                    class="fa-light fa-qrcode qr-code-icon"
                    @click="scanQrCode"
                    @keyup.enter="scanQrCode" />
                  <InputText
                    id="username"
                    ref="email"
                    v-model="email"
                    class="w-full border-0 shadow-none"
                    name="email"
                    spellcheck="false"
                    autocapitalize="off"
                    @keyup.enter="enterPress($event)"
                    @keydown.enter="$event.preventDefault()">
                  </InputText>
                  <!-- eslint-disable vuejs-accessibility/label-has-for -->
                  <label for="username">
                    {{ form === "create"
                      ? $t("user_login.email_create")
                      : $t("user_login.email") }}
                  </label>
                </span>
              </div>
              <div
                v-if="form === 'create'"
                class="mb-16 rounded-6 border border-grey-4 pt-12">
                <span class="p-float-label">
                  <InputText
                    id="name"
                    ref="name"
                    v-model="name"
                    class="w-full border-0 shadow-none"
                    name="name"
                    spellcheck="false"
                    autocapitalize="off"
                    @keyup.enter="enterPress($event)"
                    @keydown.enter="$event.preventDefault()"/>
                  <label for="name">
                    {{ form === "create"
                      ? $t("user_login.name_create")
                      : $t("user_login.name") }}
                  </label>
                </span>
              </div>
              <div
                v-if="form == 'login'"
                class="mb-16 rounded-6 border border-grey-4 pt-12">
                <span class="p-float-label">
                  <InputText
                    id="password"
                    ref="password"
                    v-model="password"
                    class="w-full border-0 shadow-none"
                    name="password"
                    type="password"
                    autocomplete="off"
                    required
                    @keyup.enter="enterPress($event)"/>
                  <!-- eslint-disable vuejs-accessibility/label-has-for -->
                  <label for="password">
                    {{ form === "create"
                      ? $t("user_login.password_create")
                      : $t("user_login.password") }}
                  </label>
                </span>
              </div>
              <div
                v-if="form !== 'create'"
                class="text-right">
                <WButton
                  type="link"
                  :label="form == 'login' ? $t('user_login.forgot') : $t('user_login.back_to_login')"
                  class="!h-20"
                  tabindex="0"
                  @click="toggleForm" />
              </div>
              <div
                v-if="form == 'login'"
                class="flex justify-center">
                <WButton
                  :label="$t('user_login.login_password')"
                  class="mt-16 w-[360px] uppercase sm:w-[400px]"
                  @click="userLogin"
                  @keyup.enter="userLogin" />
              </div>
              <div
                v-if="form == 'create'"
                class="flex justify-center">
                <WButton
                  :label="$t('user_login.create_account')"
                  class="mt-16 w-[360px] uppercase sm:w-[400px]"
                  :disabled="!email || !name"
                  @click.prevent="createAccount"
                  @keyup.enter="createAccount" />
              </div>
              <p
                v-if="error || errorText"
                class="mb-24 mt-12 bg-red-10 px-24 py-16 text-center text-red sm:mb-0"
                v-html="
                  errorText
                    ? errorText
                    : $t(`error_messages.${error}`, {
                      url: '/login?c=' + accessCode
                    })
                ">
              </p>
              <p
                v-if="success"
                class="mb-24 mt-12 bg-dark-green-10 px-24 py-16 text-center text-dark-green sm:mb-0">
                {{ $t(`success_messages.${success}`) }}
              </p>
              <hr
                v-if="form === 'login' || form === 'create'"
                class="button-separator"/>
              <WButton
                v-if="form == 'forgot'"
                :label="$t('user_login.forgot_password')"
                class="my-16 w-[360px] uppercase sm:w-[400px]"
                @click="forgotPassword"
                @keyup.enter="forgotPassword" />
              <div
                v-show="form == 'login' || form == 'create'"
                class="rounded-100 mb-16 flex justify-center">
                <GoogleLogin
                  :callback="googleSignIn"
                  :buttonConfig="buttonConfig"/>
              </div>
              <div
                v-if="form == 'login' || form == 'create'"
                class="flex flex-col gap-16">
                <IconButton
                  buttonClasses="w-[400px] max-h-[38px] !bg-[#436cf2]"
                  :image="require(`@/assets/logo-clever.png`)"
                  :label="form === 'login' ? $t('user_login.login_clever') : $t('user_login.create_clever')"
                  iconClasses="h-20 rounded-[5px]"
                  @click="cleverLogin"
                  @keyup.enter="cleverLogin">
                </IconButton>

                <IconButton
                  buttonClasses="w-[400px] max-h-[38px] !bg-[#0089D6]"
                  :image="require(`@/assets/logo-azure.png`)"
                  :label="
                    form === 'login'
                      ? $t('user_login.login_azure')
                      : $t('user_login.create_azure')
                  "
                  iconClasses="h-20 rounded-[5px]"
                  @click="azureLogin"
                  @keyup.enter="azureLogin">
                </IconButton>

                <IconButton
                  buttonClasses="w-[400px] max-h-[38px] !bg-[#26ABCD]"
                  :image="require(`@/assets/logo-classlink.png`)"
                  :label="
                    form === 'login'
                      ? $t('user_login.login_classlink')
                      : $t('user_login.create_classlink')
                  "
                  iconClasses="h-20 rounded-[5px]"
                  @click="classlinkLogin"
                  @keyup.enter="classlinkLogin">
                </IconButton>
              </div>
            </div>
          </template>
        </form>
      </div>
    </template>
  </unauthed-layout>
</template>

<script lang="js">
import {
  defineComponent
} from "vue";

import InputText from "primevue/inputtext";

import AuthService from "@/services/auth.service";
import Helpers from "@/mixins/Helpers";
import UnauthedLayout from "@/layouts/UnauthedLayout";
import WButton from "@/components/wrappers/WButton";
import IconButton from "@/components/utils/IconButton";
import ScanQrCode from "@/components/users/ScanQrCode";

import {
  decodeJwt
} from "jose";

export default defineComponent({
  name: "UserLogin",
  components: {
    InputText,
    IconButton,
    UnauthedLayout,
    WButton
  },
  mixins: [
    Helpers
  ],
  data() {
    return {
      error: null,
      errorText: null,
      form: "login",
      email: "",
      name: "",
      password: "",
      attempts: 0,
      apiCallFailed: false,
      loginFailed: false,
      createdAccount: false,
      success: null
    };
  },
  computed: {
    accessCode() {
      return this.$route.query.c;
    },
    title() {
      switch (this.form) {
      case "create":
        return this.$t("user_login.title_create");
      case "forgot":
        return this.$t("user_login.title_forgot");
      default:
        return this.$t("user_login.title");
      }
    },
    buttonConfig() {
      return {
        width: 400,
        text: "signin_with",
        logo_alignment: "center",
        locale: process.env.VUE_APP_I18N_LOCALE
      };
    }
  },
  created() {
    this.authService = new AuthService();
  },
  mounted() {
    // QR Code Login
    if (this.$route.query.direct && this.$route.query.key) {
      this.directLogin(this.$route.query.direct, this.$route.query.key);
    }
    // handle SAML LOGIN INITIALIZER
    if (this.$route.query.saml_errors) {
      let error = this.$route.query.saml_errors;
      this.error = error;
    }
    if (this.$route.query.user_access_token) {
      let token = this.$route.query.user_access_token;
      this.handleSAMLResponse(token);
    }
    if (this.$route.query["ny-district-login"]) {
      // Redirect the user to IDP Router
      let url = new URL("/users/saml/login.json?ny-district-login=true", process.env.VUE_APP_API_URL).href;
      window.location.href = url;
    }

    if (this.$route.query["philadelphia-district-login"]) {
      // Redirect the user to Philadelphia IDP Router
      let url = new URL("/users/saml/login.json?philadelphia-district-login=true", process.env.VUE_APP_API_URL).href;
      window.location.href = url;
    }

    if (this.user && this.user.email) {
      this.showHomeScreen();
    }
    else {
      this.email = this.$route.query.email || "";
      this.password = "";
      this.attempts = 0;
      this.cleverCallback();
      this.classlinkCallback();
      this.logPage();
    }

    if (this.$route.query.c && this.$route.query.create) {
      this.form = "create";
    }
  },
  methods: {
    enterPress(event) {
      if (this.form == "login") {
        this.userLogin(null);
      }
      else if (this.form == "forgot") {
        this.forgotPassword();
      }
    },
    toggleForm() {
      this.success = null;
      this.error = null;
      this.form = this.form == "login" ? "forgot" : "login";
    },
    async userLogin(event) {
      if (event) {
        event.preventDefault();
      }
      if (this.email.length == 0) {
        this.error = "user_login_enter_email";
        this.setFocus(this.$refs.email);
      }
      else if (this.password.length == 0) {
        this.error = "user_login_enter_password";
        this.setFocus(this.$refs.password);
      }
      else {
        try {
          this.showLoading("user_login.loading");
          let params = {
            email: this.email,
            password: this.password
          };
          if (this.$route.query.c && this.$route.query.c.length === 6) {
            params.code = this.$route.query.c;
          }
          let serverResponse = await this.storeDispatch("userLogin", params);
          const redirectFrom = this.$route.redirectedFrom?.fullPath;
          if (redirectFrom) {
            this.$router.push(redirectFrom);
          }
          else {
            if (serverResponse.added_classroom) {
              let classroomName = serverResponse.added_classroom.name;
              this.showHomeScreen(false, true, classroomName);
            }
            else {
              this.showHomeScreen();
            }
          }
        }
        catch (error) {
          this.attempts = this.attempts + 1;
          if (error && error.response && error.response.status == 401 && this.attempts < 50) {
            this.error = "user_login_invalid_credentials";
          }
          else if (this.attempts >= 50) {
            this.error = "user_login_too_many_attempts";
          }
          else if (error && error.response && error.response.data && error.response.data.error) {
            this.error = error.response.data.error;
          }
          else {
            this.error = "user_login_unknown_problem";
          }
        }
        finally {
          this.hideLoading();
        }
      }
    },
    async handleSAMLResponse(auth_token) {
      if (auth_token && auth_token.length > 0) {
        try {
          this.showLoading("user_login.loading");
          this.$router.replace({
            "query.user_access_token": null
          });
          let serverResponse = await this.storeDispatch("samlLogin", {
            user_access_token: auth_token
          });

          if (serverResponse.added_classroom) {
            let classroomName = serverResponse.added_classroom.name;
            this.showHomeScreen(false, true, classroomName);
          }
          else {
            this.showHomeScreen();
          }
        }
        catch (error) {
          let message = this.errorMessage(error);
          this.error = "user_login_classlink_problem";
        }
        finally {
          this.hideLoading();
        }
      }
    },
    async googleSignIn(response) {
      if (response.credential && response.credential.length > 0) {
        const jwt = response.credential;
        let classroom_code = this.$route.query.c;
        try {
          this.showLoading();
          /* Decode token without verification */
          const payload = decodeJwt(jwt);
          let serverResponse;
          try {
            serverResponse = await this.storeDispatch("googleLogin", {
              name: payload.name,
              email: payload.email,
              image: payload.picture,
              expires_at: payload.exp,
              id_token: jwt,
              classroom_code: classroom_code
            });
            if (serverResponse.added_classroom) {
              let classroomName = serverResponse.added_classroom.name;
              this.showHomeScreen(false, true, classroomName);
            }
            else {
              this.showHomeScreen();
            }
          }
          catch (error) {
            // errorText for error messages that come back without codes/keys that we can use to translate
            this.errorText = error.response.data.message;
          }
        }
        catch (error) {
          this.error = error?.response?.data?.error || this.$t("error_messages.invalid_code");
        }
        finally {
          this.hideLoading();
        }
      }
    },
    azureLogin() {
      this.loginFailed = false;
      this.authService.login().then(response => {
        if (response) {
          this.azureLoginCallback(response);
        }
        else {
          this.loginFailed = true;
        }
      }, () => {
        this.loginFailed = true;
      });
    },
    async azureLoginCallback(response) {
      if (response) {
        const jwt = response.accessToken;
        try {
          this.showLoading();
          /* Decode token without verification */
          const payload = decodeJwt(jwt);
          let classroom_code = this.$route.query.c;
          let serverResponse = await this.storeDispatch("azureLogin", {
            jwt: jwt,
            id_token: payload.oid,
            email: payload.unique_name,
            expires_at: payload.exp,
            name: payload.name,
            classroom_code: classroom_code
          });
          if (serverResponse.added_classroom) {
            let classroomName = serverResponse.added_classroom.name;
            this.showHomeScreen(false, true, classroomName);
          }
          else {
            this.showHomeScreen();
          }
        }
        catch (error) {
          if (error) {
            let message = this.errorMessage(error);
            this.error = message;
          }
        }
        finally {
          this.hideLoading();
        }
      }
    },
    classlinkLogin() {
      this.loginFailed = false;
      this.openUrlSameWindow(process.env.VUE_APP_CLASSLINK_LAUNCHPAD_URI);
    },
    async classlinkCallback() {
      if (this.$route.query.code && this.$route.query.code.length > 0 && this.$route.query.state == "classlink") {
        let code = this.$route.query.code;
        let classroom_code = this.$route.query.c;
        try {
          this.showLoading("user_login.loading");
          this.$router.replace({
            "query.code": null
          });
          let serverResponse = await this.storeDispatch("classlinkLogin", {
            code: code,
            classroom_code: classroom_code
          });
          if (serverResponse.added_classroom) {
            let classroomName = serverResponse.added_classroom.name;
            this.showHomeScreen(false, true, classroomName);
          }
          else {
            this.showHomeScreen();
          }
        }
        catch (error) {
          let message = this.errorMessage(error);
          this.error = "user_login_classlink_problem";
        }
        finally {
          this.hideLoading();
        }
      }
    },
    cleverLogin() {
      let params = {
        response_type: "code",
        client_id: process.env.VUE_APP_CLEVER_CLIENT_ID,
        redirect_uri: process.env.VUE_APP_REDIRECT_URI
      };
      if (process.env.VUE_APP_TEST_CLEVER_DISTRICT_ID && process.env.VUE_APP_TEST_CLEVER_DISTRICT_ID.length > 0) {
        params["district_id"] = process.env.VUE_APP_TEST_CLEVER_DISTRICT_ID;
      }
      this.openUrl(`https://clever.com/oauth/authorize?${Object.keys(params).map(key => key + "=" + params[key]).join("&")}`);
    },
    async cleverCallback() {
      if (this.$route.query.code && this.$route.query.code.length > 0 && (this.$route.query.state !== "classlink")) {
        try {
          this.showLoading("user_login.loading");
          let code = this.$route.query.code;
          let classroom_code = this.$route.query.c;
          this.$router.replace({
            "query.code": null
          });
          let serverResponse = await this.storeDispatch("cleverLogin", {
            code: code,
            redirect_uri: process.env.VUE_APP_REDIRECT_URI,
            classroom_code: classroom_code
          });
          if (serverResponse.added_classroom) {
            let classroomName = serverResponse.added_classroom.name;
            this.showHomeScreen(false, true, classroomName);
          }
          else {
            this.showHomeScreen();
          }
        }
        catch (error) {
          let message = this.errorMessage(error);
          this.error = "user_login_clever_problem";
        }
        finally {
          this.hideLoading();
        }
      }
    },
    async directLogin(direct, key) {
      await this.storeDispatch("userLogin", {
        direct,
        key
      });

      this.showHomeScreen();
    },
    async forgotPassword() {
      if (this.email.length == 0) {
        this.error = "user_login_enter_email";
        this.setFocus(this.$refs.email);
      }
      else if (/\S+@\S+\.\S+/.test(this.email) == false) {
        this.error = "invalid_email_format";
        this.setFocus(this.$refs.email);
      }
      else {
        try {
          this.showLoading();
          await this.storeDispatch("forgotPassword", {
            email: this.email
          });
          this.email = "";
          this.password = "";
          this.success = "forgot_password_complete";
          this.error = null;
        }
        catch (error) {
          if (error && error.response && (error.response.status == 403 || error.response.status == 429)) {
            this.error = "user_login_too_many_reset_attempts";
          }
          else {
            this.error = error;
          }
        }
        finally {
          this.hideLoading();
        }
      }
    },
    async createAccount() {
      let classroomCode = this.$route.query.c;
      try {
        this.showLoading("user_login.creating");
        const response = await this.storeDispatch("registerNewStudent", {
          email: this.email,
          name: this.name,
          code: classroomCode
        });
        this.createdAccount = true;
      }
      catch (error) {
        this.error = error.response?.data?.error;
      }
      finally {
        this.hideLoading();
      }
    },
    scanQrCode() {
      this.openDialog(ScanQrCode, "large");
    }
  }
});
</script>

<style scoped lang="scss">
.button-separator {
  @apply text-grey-1;
  overflow: visible;
  text-align: center;
  margin: 35px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  &:after {
    content: "Or";
    position: relative;
    background: white;
    top: -11px;
    padding: 0 10px;
  }
}
#subtitle {
  @apply text-dark-green;
  display: block;
  font-size: 16px;
  margin: 10px 0 15px;
  text-align: left;
}
h1 {
  @apply text-dark-green font-lora;
  font-size: 31px;
  font-weight: bold;
  margin: 0;
  padding: 0;
  text-align: left;
}
label {
  font-size: 14px !important;
}

.p-float-label label {
  top: 18px;
}
.p-float-label input:focus ~ label,
.p-float-label input.p-filled ~ label {
  top: 2px;
  font-size: 10px !important;
}

.qr-code-icon {
  @apply absolute right-[14px] cursor-pointer text-[24px] hover:text-grey-3;

  top: 38%;
  transform: translateY(-50%);
}

/*** Smaller sizes adjustments ***/
@media screen and (max-width: 700px) {
  h1 {
    font-size: 25px;
  }
}
</style>
